import { Button, Icon, Text, Skeleton, HStack, Stack, VStack } from '@genial/components';
import { Color, ColorTypes, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import React from 'react';

export namespace AverageTicketBigNumberCardTypes {
  export interface Props {
    title: string;
    value?: number;
    assetName: string;
    assetValue?: number;
    assetPercentage?: number;
    loading?: boolean;
    error?: Error;
    refresher?: () => void;
  }
}

export const AverageTicketBigNumberCard: React.FC<AverageTicketBigNumberCardTypes.Props> = ({
  assetPercentage,
  assetValue,
  assetName,
  value,
  title,
  error,
  refresher,
  loading,
}) => {
  // NOTE: Minus sign is usually added automatically
  const percentageSign = (assetPercentage ?? 0) > 0 ? '+' : '';
  const textColor: ColorTypes.Option = (value ?? 0) < 0 ? 'feedback.error.default' : 'neutral.dark';
  const textAlign = useBreakpointValue({
    base: 'left',
    sm: 'center',
  });

  if (loading) {
    return (
      <VStack space={Spacing['stack.quarck']} flex={1}>
        <Skeleton height="stack.xxxs" width="100%" borderRadius="xs" />
        <Skeleton height="stack.xxxs" width="100%" borderRadius="xs" />
        <HStack space={Spacing['stack.quarck']}>
          <Skeleton height="stack.xxxs" flex={1} borderRadius="xs" />
          <Skeleton height="stack.xxxs" flex={1} borderRadius="xs" />
        </HStack>
      </VStack>
    );
  }

  if (error) {
    return (
      <VStack
        padding={Spacing['inset.quarck']}
        alignItems="center"
        space={Spacing['stack.quarck']}
        flex={1}>
        <HStack space={Spacing['stack.nano']} alignItems="center">
          <Icon name="Warning" size="xxs" color={Color['feedback.attention.default']} />
          <Text>Não foi possível carregar os dados</Text>
        </HStack>
        <Button
          padding={0}
          p={0}
          paddingX={0}
          justifyContent={'flex-start'}
          size={'sm'}
          variant="textLink"
          rightIcon={<Icon name="Refresh" size="xxs" color={Color['brand.tertiary.principal']} />}
          onPress={refresher}>
          Recarregar
        </Button>
      </VStack>
    );
  }

  return (
    <VStack space={Spacing['stack.quarck']}>
      <Stack direction={{ base: 'row', sm: 'column' }} space={Spacing['stack.quarck']}>
        <Text
          textAlign={textAlign}
          flex={1}
          color="neutral.medium"
          fontSize={Typography['fontsize.md']}
          variant="semibold">
          {title}
        </Text>
        <Text
          textAlign={textAlign}
          fontSize={Typography['fontsize.lg']}
          color={textColor}
          variant="semibold">
          {Utils.Mask.ScaleMoney(value)}
        </Text>
      </Stack>
      <HStack flex={1} space={Spacing['stack.quarck']}>
        <Text color="neutral.medium">{assetName}:</Text>
        <Text color="neutral.medium">{Utils.Mask.ScaleMoney(assetValue)}</Text>
        <Text
          flex={{ base: 1, sm: 'inherit' }}
          variant="bold"
          textAlign={{ base: 'right', sm: 'inherit' }}
          color="neutral.medium">
          ({percentageSign} {Utils.Mask.Percentage(assetPercentage)})
        </Text>
      </HStack>
    </VStack>
  );
};
